"use client";

import { subscribeToNotifications } from "@/services/NotificationService";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { toast } from "sonner";

interface PWAContextType {
  isSupported: boolean;
  isInstalled: boolean;
  canInstall: boolean;
  isSubscribed: boolean;
  subscription: PushSubscription | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  install: () => Promise<void>;
  isLoading: boolean;
}

const PWAContext = createContext<PWAContextType | null>(null);

interface PWAProviderProps {
  children: ReactNode;
}

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PWAProvider({ children }: PWAProviderProps) {
  const [isSupported, setIsSupported] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isServiceWorkerSupported = "serviceWorker" in navigator;
      const isPushSupported = "PushManager" in window;
      const isNotificationSupported = "Notification" in window;

      setIsSupported(isServiceWorkerSupported && isPushSupported && isNotificationSupported);

      const mediaQuery = window.matchMedia("(display-mode: standalone)");
      setIsInstalled(mediaQuery.matches);

      const handleDisplayModeChange = (e: MediaQueryListEvent) => {
        setIsInstalled(e.matches);
      };

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleDisplayModeChange);
      } else {
        mediaQuery.addListener(handleDisplayModeChange);
      }

      const handleBeforeInstallPrompt = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setCanInstall(true);
      };

      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener("change", handleDisplayModeChange);
        } else {
          mediaQuery.removeListener(handleDisplayModeChange);
        }
        window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      };
    }
  }, []);

  useEffect(() => {
    if (isSupported) {
      initializePWA();
    }
  }, [isSupported]);

  const initializePWA = async () => {
    try {
      await registerServiceWorker();
    } catch (error) {
      console.error("[PWA] Initialization failed:", error);
    }
  };

  const registerServiceWorker = async () => {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
        updateViaCache: "none",
      });

      // reg.addEventListener("updatefound", () => {
      //   const newWorker = reg.installing;
      //   if (newWorker) {
      //     newWorker.addEventListener("statechange", () => {
      //       if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
      //         toast.info("New version available!", {
      //           action: {
      //             label: "Update",
      //             onClick: () => {
      //               newWorker.postMessage({ type: "SKIP_WAITING" });
      //               window.location.reload();
      //             },
      //           },
      //         });
      //       }
      //     });
      //   }
      // });

      await checkExistingSubscription(reg);

      console.log("[PWA] Service Worker registered successfully");
    } catch (error) {
      console.error("[PWA] Service Worker registration failed:", error);
    }
  };

  const checkExistingSubscription = async (reg: ServiceWorkerRegistration) => {
    try {
      setIsLoading(true);
      const existingSubscription = await reg.pushManager.getSubscription();
      if (existingSubscription) {
        console.log("🚀 ~ checkExistingSubscription ~ existingSubscription:", existingSubscription);
        setSubscription(existingSubscription);
        setIsSubscribed(true);
      }
    } catch (error) {
      console.error("[PWA] Error checking existing subscription:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const subscribe = async () => {
    setIsLoading(true);
    const currentRegistration = await navigator.serviceWorker.ready;
    if (!currentRegistration || !VAPID_PUBLIC_KEY) {
      toast.error("Push browser notifications not properly configured");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("Notification permission denied");
        return;
      }

      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const pushSubscription = await currentRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as any,
      });

      await subscribeToNotifications({
        webPushSubscription: pushSubscription,
      });

      setSubscription(pushSubscription);
      setIsSubscribed(true);
    } catch (error) {
      console.error("[PWA] Error subscribing to push notifications:", error);
      toast.error("Failed to enable push notifications");
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async () => {
    setIsLoading(true);
    if (!subscription) {
      toast.error("No active subscription to disable");
      setIsLoading(false);
      return;
    }

    try {
      await subscription.unsubscribe();
      setSubscription(null);
      setIsSubscribed(false);
      toast.success("Browser notifications disabled successfully");
    } catch (error) {
      console.error("[PWA] Error unsubscribing from push notifications:", error);
      toast.error("Failed to disable browser notifications");
    } finally {
      setIsLoading(false);
    }
  };

  const install = async () => {
    if (!deferredPrompt) {
      toast.error("App installation not available");
      return;
    }

    try {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;

      if (choiceResult.outcome === "accepted") {
        toast.success("App installed successfully!");
        setCanInstall(false);
        setIsInstalled(true);
      } else {
        toast.info("App installation cancelled");
      }

      setDeferredPrompt(null);
    } catch (error) {
      console.error("[PWA] Error during app installation:", error);
      toast.error("Failed to install app");
    }
  };

  const contextValue: PWAContextType = {
    isSupported,
    isInstalled,
    canInstall,
    isSubscribed,
    subscription,
    subscribe,
    unsubscribe,
    install,
    isLoading,
  };

  return <PWAContext.Provider value={contextValue}>{children}</PWAContext.Provider>;
}

export function usePWA() {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error("usePWA must be used within a PWAProvider");
  }
  return context;
}
